import React from 'react';

const NeuroPrintSummary = ({ data, diagnosisData, zone, isSubmitted }) => (
  <>
    <div style={{ borderBottom: '2px solid #4f46e5', paddingBottom: 12, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>AssessMonk</div>
            <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Physiotherapy Assessment</div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>assessmonk.com</div>
      </div>
      <h1 style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 800 }}>🧠 Neurological Assessment — {zone || 'Unspecified Pattern'}</h1>
      <div style={{ fontSize: 11, color: '#64748b' }}>Printed: {new Date().toLocaleString()}</div>
    </div>

    <section style={{ marginBottom: 14 }}>
      <h2 style={{ fontSize: 13, fontWeight: 800, background: '#eff6ff', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>👤 Patient Information</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
        {[['Name', data.name], ['Age', data.age], ['Gender', data.gender], ['Occupation', data.occupation],
          ['Reg No.', data.regNo], ['Date', data.date], ['Type', data.ipOp], ['Phone', data.phone],
          ['Civil Status', data.civilStatus], ['Address', data.address], ['Dominant Hand', data.dominantHand],
          ['Medical Diagnosis', data.diagnosis],
        ].filter(([, v]) => v).map(([l, v]) => (
          <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
        ))}
      </div>
    </section>

    {(data.chiefComplaint || data.hpiText || data.symOnset || data.symSeverity) && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f5f3ff', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>📋 Subjective History</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '4px 16px' }}>
          {[['Chief Complaint', data.chiefComplaint], ['HPI', data.hpiText], ['Medical History', data.historyMedical],
            ['Personal History', data.historyPersonal], ['Family History', data.historyFamily],
            ['Side', data.symSide], ['Onset', data.symOnset], ['Duration', data.symDuration],
            ['Severity', data.symSeverity ? `${data.symSeverity}/10` : ''],
            ['Cause', Array.isArray(data.symCause) ? data.symCause.join(', ') : data.symCause],
          ].filter(([, v]) => v && v !== '0').map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {(data.mmtState || data.cranialData || data.reflexData || data.gaitData) && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#ecfdf5', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🔬 Objective Examination</h2>
        {[
          ['MMT', data.mmtState], ['Cranial Nerves', data.cranialData], ['Sensory', data.sensoryData],
          ['Tone', data.toneState], ['MMSE', data.mmseData], ['FIM', data.fimData],
          ['Voluntary Control', data.voluntaryControlData], ['ROM', data.romData],
          ['Reflexes', data.reflexData], ['Coordination', data.coordinationData],
          ['Balance', data.balanceData], ['Posture', data.postureData],
          ['Gait', data.gaitData], ['Hand Function', data.handData], ['Systems Review', data.systemsData],
        ].filter(([, v]) => v && typeof v === 'object' && Object.keys(v).length > 0).map(([l, v]) => (
          <div key={l} style={{ marginBottom: 6 }}>
            <strong>{l}:</strong>
            {Object.entries(v).filter(([, val]) => val !== '' && val !== null && val !== undefined).map(([k, val]) => (
              <span key={k} style={{ marginLeft: 8 }}>{k}: {Array.isArray(val) ? val.join(', ') : String(val)}</span>
            ))}
          </div>
        ))}
      </section>
    )}

    {data.investigation_list?.length > 0 && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#fef9c3', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🩻 Investigations</h2>
        {data.investigation_list.map((r, i) => (
          <div key={i} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{r.type}: </span>{r.finding}</div>
        ))}
      </section>
    )}

    {data.problem_list?.length > 0 && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f1f5f9', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>▪ Problem List</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              {['IMPAIRMENT', 'FUNCTIONAL LIMITATION', 'DISABILITY'].map(h => (
                <th key={h} style={{ border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'left', fontWeight: 800, textDecoration: 'underline', background: '#f8fafc' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.problem_list.map((row, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid #cbd5e1', padding: '6px 8px', verticalAlign: 'top' }}>{row.impairment}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '6px 8px', verticalAlign: 'top' }}>{row.functionalLimitation}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '6px 8px', verticalAlign: 'top' }}>{row.disability}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )}

    {data.functionalDiagnosis && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f1f5f9', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>▪ Functional Diagnosis</h2>
        <div>{data.functionalDiagnosis}</div>
      </section>
    )}

    {(data.shortTermGoals || data.longTermGoals) && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f1f5f9', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>▪ Goals</h2>
        <table style={{ width: '60%', borderCollapse: 'collapse', fontSize: 11 }}>
          <tbody>
            {[['Short Term Goals', data.shortTermGoals], ['Long Term Goals', data.longTermGoals]].filter(([, v]) => v).map(([label, val]) => (
              <tr key={label}>
                <td style={{ border: '1px solid #cbd5e1', padding: '6px 10px', fontWeight: 700, background: '#f8fafc', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{label}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '6px 10px' }}>{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )}

    {data.treatmentPlan && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f1f5f9', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>▪ Treatment Plan</h2>
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{data.treatmentPlan}</div>
      </section>
    )}

    {isSubmitted && (diagnosisData?.lesionLocalization || diagnosisData?.umnLmnClassification) && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#eff6ff', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🧠 Neurological Pattern Analysis</h2>
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: '0.72rem', color: '#92400e', lineHeight: 1.5 }}>
          <strong>AI-generated content — clinical review required.</strong> This analysis is produced by an AI model and may contain errors or omissions. It is intended to support, not replace, clinical judgement. Always verify findings against the patient's full clinical picture before acting.
        </div>
        {data.diagnosis && <div style={{ marginBottom: 6 }}><strong>Medical Diagnosis (Doctor-Given):</strong> {data.diagnosis}</div>}
        {diagnosisData.lesionLocalization && <div style={{ marginBottom: 6 }}><strong>Lesion Localization:</strong> {diagnosisData.lesionLocalization}</div>}
        {diagnosisData.umnLmnClassification && <div style={{ marginBottom: 6 }}><strong>UMN / LMN Classification:</strong> {diagnosisData.umnLmnClassification}</div>}
        {diagnosisData.impairmentDistribution && <div style={{ marginBottom: 6 }}><strong>Impairment Distribution:</strong> {diagnosisData.impairmentDistribution}</div>}
        {diagnosisData.functionalConsequences?.length > 0 && (
          <div style={{ marginBottom: 6 }}><strong>Functional Consequences:</strong>{diagnosisData.functionalConsequences.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
        {diagnosisData.rehabPriorities?.length > 0 && (
          <div style={{ marginBottom: 6 }}><strong>Rehab Priorities:</strong>{diagnosisData.rehabPriorities.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
        {diagnosisData.prognosisIndicators?.length > 0 && (
          <div style={{ marginBottom: 6 }}><strong>Prognosis Indicators:</strong>{diagnosisData.prognosisIndicators.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
        {diagnosisData.clinicalReasoning?.length > 0 && (
          <div><strong>Clinical Reasoning:</strong>{diagnosisData.clinicalReasoning.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
      </section>
    )}
  </>
);

export default NeuroPrintSummary;
